import { useState } from "react";
import { Wand2, Copy, Loader2, Briefcase, ClipboardCheck, FileText } from "lucide-react";
import { toast } from "sonner";
import { streamAi } from "@/lib/aiStream";
import { AiMarkdown } from "@/components/ai/AiMarkdown";

const TOOLS = {
    job_description: { label: "Job description", icon: Briefcase, detailsLabel: "Extra notes (optional)", detailsHint: "e.g. 3+ years experience, based in Lekki, hybrid" },
    review: { label: "Improve review", icon: ClipboardCheck, detailsLabel: "Your appraisal comments", detailsHint: "Paste the comments you want to improve" },
    letter: { label: "Letter or memo", icon: FileText, detailsLabel: "What should it say?", detailsHint: "e.g. Confirm Ada Obi as Sales Executive from 1 July, salary to be reviewed in December" },
};
const TONES = ["professional", "warm", "firm"];
const field = "w-full rounded-xl border border-ink/15 px-4 py-2.5 text-sm focus:outline-none focus:border-klein";

export const AiDraftTool = () => {
    const [kind, setKind] = useState("job_description");
    const [form, setForm] = useState({ title: "", department: "", details: "", tone: "professional" });
    const [out, setOut] = useState("");
    const [busy, setBusy] = useState(false);
    const tool = TOOLS[kind];
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const run = async (e) => {
        e.preventDefault();
        setBusy(true);
        setOut("");
        try {
            await streamAi("/ai/draft", { kind, ...form }, { onDelta: (d) => setOut((o) => o + d) });
        } catch (err) {
            toast.error(err.message);
        } finally {
            setBusy(false);
        }
    };

    const copy = () => navigator.clipboard.writeText(out).then(() => toast.success("Copied to clipboard"));

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="ai-draft-tool">
            <form onSubmit={run} className="bg-white border border-ink/10 p-5 sm:p-6 space-y-4">
                <div className="flex flex-wrap gap-2">
                    {Object.entries(TOOLS).map(([k, t]) => (
                        <button type="button" key={k} onClick={() => { setKind(k); setOut(""); }} data-testid={`ai-tool-${k}`}
                            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition-colors ${k === kind ? "bg-ink text-white" : "border border-ink/15 text-ink/70 hover:border-ink/40"}`}>
                            <t.icon className="w-3.5 h-3.5" /> {t.label}
                        </button>
                    ))}
                </div>
                {kind === "job_description" && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <input value={form.title} onChange={set("title")} placeholder="Job title" className={field} data-testid="ai-draft-title" />
                        <input value={form.department} onChange={set("department")} placeholder="Department (optional)" className={field} data-testid="ai-draft-department" />
                    </div>
                )}
                {kind === "letter" && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <input value={form.title} onChange={set("title")} placeholder="Type, e.g. confirmation letter, query, memo" className={field} data-testid="ai-draft-letter-type" />
                        <select value={form.tone} onChange={set("tone")} className={field} data-testid="ai-draft-tone">
                            {TONES.map((t) => <option key={t} value={t}>{t[0].toUpperCase() + t.slice(1)} tone</option>)}
                        </select>
                    </div>
                )}
                <div>
                    <label className="text-xs font-semibold text-ink/60">{tool.detailsLabel}</label>
                    <textarea value={form.details} onChange={set("details")} rows={kind === "job_description" ? 3 : 7} maxLength={4000}
                        placeholder={tool.detailsHint} className={`${field} mt-1.5 resize-y`} data-testid="ai-draft-details" />
                </div>
                <button type="submit" disabled={busy} className="inline-flex items-center gap-2 bg-klein text-white px-5 py-2.5 text-sm hover:bg-klein/90 disabled:opacity-60" data-testid="ai-draft-generate-btn">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} {busy ? "Writing…" : "Write it"}
                </button>
            </form>
            <div className="bg-white border border-ink/10 p-5 sm:p-6 flex flex-col min-h-[320px]" data-testid="ai-draft-output">
                <div className="flex items-center justify-between">
                    <div className="overline">Draft</div>
                    {out && !busy && (
                        <button onClick={copy} className="inline-flex items-center gap-1.5 text-xs font-semibold text-klein" data-testid="ai-draft-copy-btn"><Copy className="w-3.5 h-3.5" /> Copy</button>
                    )}
                </div>
                <div className="mt-3 flex-1 overflow-y-auto">
                    {out ? <AiMarkdown text={out} /> : <p className="text-sm text-ink/40">{busy ? "Writing your draft…" : "Your draft will appear here. Always read it through before you use it."}</p>}
                </div>
            </div>
        </div>
    );
};

export default AiDraftTool;
